import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { PreFilterService, SubscriptionService } from "../../api/services";
import { US_PreFilter, US_Interactive, PfkRegionState, PfkOccupationMajorGroup, PfkIndustrySector } from "../../api/models";
import { UserState } from "../../api/models/user-state";
import { GlobalApp } from "../../tools/global";
import { MatDialog } from "@angular/material/dialog";
import { InputEditDialogComponent } from "../../dialogs/input-edit-dialog/input-edit-dialog.component";
import { DialogResultType } from "../../dialogs/DialogResult";
import { ConfirmDialogComponent } from "../../dialogs/confirm-dialog/confirm-dialog.component";
import { PreFilterTextDialogComponent } from "./dialogs/pre-filter-text-dialog/pre-filter-text-dialog.component";
import { PreFilterRegionDialogComponent } from "./dialogs/pre-filter-region-dialog/pre-filter-region-dialog.component";
import { PreFilterOccupationDialogComponent } from
  "./dialogs/pre-filter-occupation-dialog/pre-filter-occupation-dialog.component";
import { PreFilterIndustryDialogComponent } from
  "./dialogs/pre-filter-industry-dialog/pre-filter-industry-dialog.component";
import { PreFilterEditorDialogComponent } from
  "../../dialogs/pre-filter-editor-dialog/pre-filter-editor-dialog.component";
import { CacheLoadState, PfkLinkedinIndustry, PreFilterValuesService } from "../../services/pre-filter-values-service";
import { MessageDialogComponent } from "../../dialogs/message-dialog/message-dialog.component";

@Component({
  selector: "pre-filter-editor",
  templateUrl: "./pre-filter-editor.component.html",
  styleUrls: ["./pre-filter-editor.component.css"]
})
export class PreFilterEditorComponent implements OnInit, OnDestroy {
  @Input()
  public subscriptionId = 0;

  public selectedPreFilterId: number = 0;

  public selectedPreFilter: US_PreFilter = {
    name: '',
    userId: 0,
    subscriptionId: 0,
    preFilterId: 0,
    preFilterValues: [],
    preFilterInteractives: [],
    isDefault: false,
    titlesFuzzyAnd: false,
    companiesFuzzyAnd: false
  };

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    preFilteredInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false
  };
  userStateSubscription: Subscription = null;


  keyValueType: string = "Company";

  selectedInteractiveId: number = 0;
  availableInteractives: US_Interactive[] = [];
  appliedInteractives: US_Interactive[] = [];

  regions: PfkRegionState[] = [];
  occupations: PfkOccupationMajorGroup[] = [];
  industries: PfkLinkedinIndustry[] = [];

  preFilterDataLoaded: boolean = false;

  cacheLoadState: CacheLoadState | null = null;

  isReportSubscription: boolean = true;

  constructor(
    private subscriptionService: SubscriptionService,
    private preFilterService: PreFilterService,
    private preFilterValuesService: PreFilterValuesService,
    private app: GlobalApp,
    private dialog: MatDialog,
  ) {

    this.preFilterDataLoaded = this.preFilterValuesService.allDataLoaded();
    this.cacheLoadState = this.preFilterValuesService.getCacheLoadState();

    this.preFilterValuesService.allDataLoadedChange.subscribe((isAvailable) => {
      this.preFilterDataLoaded = isAvailable;
    });

    this.preFilterValuesService.cacheLoadStateChanged.subscribe((currentState) => {
      this.cacheLoadState = currentState;
    });
  }


  ngOnInit(): void {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;
      this.loadPreFilterData();
      this.app.popLoading();
    });

    this.regions = this.preFilterValuesService.getRegionValues();
    this.occupations = this.preFilterValuesService.getOccupationValues();
    this.industries = this.preFilterValuesService.getIndustryValues();
  }

  ngOnDestroy(): void {
    this.userStateSubscription.unsubscribe();
  }

  public loadPreFilterData(): void {

    this.subscriptionService.subscriptionGetSubscriptionIsReport(this.subscriptionId).subscribe((result) => {
      this.isReportSubscription = result;
    });

    if (this.selectedPreFilterId === 0) {
      this.clearPreFilterData();
    } else {
      this.preFilterService.preFilterGetPreFilterById(this.selectedPreFilterId).subscribe(result => {
        this.selectedPreFilter = result;

        this.availableInteractives = [];
        this.appliedInteractives = [];

        this.userState.interactives.forEach(interactive => {
          var existing = this.selectedPreFilter.preFilterInteractives.find(x => x.interactiveId == interactive.interactId);
          if (existing) {
            this.appliedInteractives.push(interactive);
          } else {
            this.availableInteractives.push(interactive);
          }
        });
      });
    }
  }

  public clearPreFilterData(): void {
    this.selectedPreFilterId = 0;
    this.selectedPreFilter = {
      name: '',
      userId: 0,
      subscriptionId: 0,
      preFilterId: 0,
      preFilterValues: [],
      preFilterInteractives: [],
      isDefault: false,
      titlesFuzzyAnd: false,
      companiesFuzzyAnd: false
    };
  }

  public showWaitMessage() {
    MessageDialogComponent.showDialog(this.dialog, "Please Wait", "Pre-Filter Values are still loading, please try again in 30 seconds.  It could take a few minutes to pre-cache all the data.  This only needs to be done once per week.");
  }

  public createNewPreFilter(): boolean {
    if (!this.preFilterDataLoaded) {
      this.showWaitMessage();
    } else {
      InputEditDialogComponent.showDialog(this.dialog, "Create Pre Filter", "Enter New Pre Filter Name", "").subscribe(result => {
        if (result.ResultType === DialogResultType.Ok) {
          var newName = result.ResultData as string;

          this.app.pushLoading("Creating Pre Filter...");
          this.preFilterService.preFilterCreatePreFilter({
            preFilterId: 0,
            userId: 0,
            subscriptionId: this.subscriptionId,
            name: newName,
            isDefault: false,
            titlesFuzzyAnd: false,
            companiesFuzzyAnd: false
          }).subscribe(result => {
            this.selectedPreFilterId = result.preFilterId;
            this.app.refreshUserState(this.subscriptionService);
            this.loadPreFilterData();
          });
        }
      });
    }

    return false;
  }

  public makePreFilterDefault(): boolean {
    this.userState.preFilters.filter(x => x.subscriptionId === this.selectedPreFilter.subscriptionId).forEach(x => x.isDefault = false);
    this.preFilterService.preFilterSetPreFilterDefault(this.selectedPreFilter.preFilterId).subscribe(result => {
      this.selectedPreFilter.isDefault = true;
    });

    return false;
  }

  public deletePreFilter(): boolean {
    ConfirmDialogComponent.showDialog(this.dialog, "Confirm Delete", "Are you sure you want to delete this pre filter?")
      .subscribe(result => {
        if (result.ResultType === DialogResultType.Ok) {
          this.app.pushLoading("Deleting Pre Filter...");
          this.preFilterService.preFilterDeletePreFilter(this.selectedPreFilter).subscribe(result => {
            this.selectedPreFilterId = 0;
            this.app.refreshUserState(this.subscriptionService);
            this.loadPreFilterData();
          });
        }
      });
    return false;
  }

  public addAppliedInteractive(): boolean {
    this.app.pushLoading("Adding Pre Filter Availability to Interactive...");
    var selectedInteractive = this.availableInteractives.find(x => x.interactId === this.selectedInteractiveId);

    this.preFilterService.preFilterCreatePreFilterInteractive({
      preFilterId: this.selectedPreFilter.preFilterId,
      interactiveId: selectedInteractive.interactId
    }).subscribe(result => {
      this.appliedInteractives.push(selectedInteractive);
      this.availableInteractives = this.availableInteractives.filter(x => {
        return x.interactId !== selectedInteractive.interactId;
      });
      this.selectedInteractiveId = 0;
      this.app.popLoading();
    }, error => {
      this.app.popLoading();
    });

    return false;
  }

  public removeAppliedInteractive(interactiveId: number): boolean {
    this.app.pushLoading("Removing Pre Filter Availability from Interactive...");
    var selectedInteractive = this.appliedInteractives.find(x => x.interactId === interactiveId);

    this.preFilterService.preFilterDeletePreFilterInteractive({
      preFilterId: this.selectedPreFilter.preFilterId,
      interactiveId: selectedInteractive.interactId
    }).subscribe(result => {
        this.availableInteractives.push(selectedInteractive);
        this.appliedInteractives = this.appliedInteractives.filter(x => {
          return x.interactId !== selectedInteractive.interactId;
        });
        this.selectedInteractiveId = 0;
        this.app.popLoading();
      },
      error => {
        this.app.popLoading();
      });

    return false;
  }

  editValues(): boolean {
    if (!this.preFilterDataLoaded) {
      this.showWaitMessage();
    } else {
      PreFilterEditorDialogComponent.showDialog(this.dialog, this.selectedPreFilterId).subscribe(result => {

      });
    }

    return false;
  }
}
